// v2.5.187 — useCurrentSeason
// Single source of truth for "which season am I working in" on a team.
// Backed by GET/PUT /api/teams/:teamId/current-season (per-user, synced,
// migration 090). Mounted once per layout, spread into Outlet context so
// every season-aware page reads the same value.
//   null   = "All seasons" (no filter) — legacy.
//   <uuid> = scope every page to that season.
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api.js';

export function useCurrentSeason(teamId) {
  const [currentSeasonId, setCurrentSeasonId]     = useState(null);
  const [currentSeasonName, setCurrentSeasonName] = useState(null);
  const [seasonLoaded, setSeasonLoaded]           = useState(false);
  const reqTeamRef = useRef(teamId); // guard stale responses on fast team switches

  useEffect(() => {
    reqTeamRef.current = teamId;
    if (!teamId) { setCurrentSeasonId(null); setCurrentSeasonName(null); setSeasonLoaded(true); return; }
    setSeasonLoaded(false);
    let cancelled = false;
    api.get(`/teams/${teamId}/current-season`)
      .then(r => {
        if (cancelled || reqTeamRef.current !== teamId) return;
        setCurrentSeasonId(r.data?.currentSeasonId || null);
        setCurrentSeasonName(r.data?.currentSeasonName || null);
      })
      .catch(() => { if (!cancelled) { setCurrentSeasonId(null); setCurrentSeasonName(null); } })
      .finally(() => { if (!cancelled) setSeasonLoaded(true); });
    return () => { cancelled = true; };
  }, [teamId]);

  // setCurrentSeason(seasonId | null, seasonName?). Optimistic; reverts on failure.
  const setCurrentSeason = useCallback((seasonId, seasonName = null) => {
    if (!teamId) return;
    const prevId = currentSeasonId;
    const prevName = currentSeasonName;
    const next = seasonId || null;
    setCurrentSeasonId(next);
    setCurrentSeasonName(next ? seasonName : null);
    api.put(`/teams/${teamId}/current-season`, { seasonId: next })
      .catch(() => { setCurrentSeasonId(prevId); setCurrentSeasonName(prevName); });
  }, [teamId, currentSeasonId, currentSeasonName]);

  return { currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded };
}

export default useCurrentSeason;
